import { useState } from 'react';
import { X, Crown, Key } from 'lucide-react';
import ActivationModal from './ActivationModal';
import { isDesktop } from '../lib/platform';
import { openExternalUrl, SCRIVAR_BILLING_URL } from '../lib/external';

/**
 * Modal shown when a free user hits their daily task limit (3 tasks/day).
 * Surfaces on web (anonymous + signed-in free) and — since the 2b-ii trial
 * onboarding work — inside the desktop app for expired-trial users, because
 * webUsage.usageCapApplies() now caps the post-trial desktop Free state too.
 *
 *   - Web: the primary CTA links to the /pricing marketing page; the
 *     registration-code path unlocks full web access.
 *   - Desktop: /pricing isn't a route in the Electron build, and "get Scrivar
 *     Desktop" is nonsensical (they're already on it). So the primary CTA opens
 *     scrivar.com/account/billing in the system browser to purchase a lifetime
 *     license (Lite $49 · Pro $70), mirroring the expired TrialBanner upsell.
 */
export default function FreeTierBanner({ open, onClose }) {
  const [showActivation, setShowActivation] = useState(false);

  if (!open) return null;

  if (showActivation) {
    return (
      <ActivationModal
        open={true}
        onClose={() => {
          setShowActivation(false);
          onClose();
        }}
        onActivated={() => {
          setShowActivation(false);
          onClose();
          // Reload to clear limit state
          window.location.reload();
        }}
      />
    );
  }

  const desktop = isDesktop();
  // Shared look for the gradient primary CTA — rendered as a link on web
  // (/pricing) and a button on desktop (opens billing in the system browser).
  const ctaClassName = 'flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium text-white';
  const ctaStyle = { background: 'linear-gradient(135deg, #4F46E5, #6366F1)' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-amber-500" />
            <h2 className="text-lg font-semibold">Daily Limit Reached</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-gray-700 mb-2">
            You've used your <strong>3 free tasks</strong> for today.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            {desktop
              ? 'Unlock every tool with a lifetime license — Lite $49 or Pro $70 (one-time) — or enter a registration code to unlock.'
              : 'Get unlimited access with Scrivar Desktop, or enter a registration code to unlock full web access.'}
          </p>

          <div className="flex flex-col gap-3">
            {desktop ? (
              <button onClick={() => openExternalUrl(SCRIVAR_BILLING_URL)} className={ctaClassName} style={ctaStyle}>
                <Crown className="w-4 h-4" />
                View Plans
              </button>
            ) : (
              <a href="/pricing" className={ctaClassName} style={ctaStyle}>
                <Crown className="w-4 h-4" />
                View Plans
              </a>
            )}

            <button
              onClick={() => setShowActivation(true)}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium border border-gray-300 text-gray-700 hover:border-indigo-500 hover:text-indigo-600 transition-colors"
            >
              <Key className="w-4 h-4" />
              Enter Registration Code
            </button>
          </div>

          <p className="text-xs text-gray-400 mt-4 text-center">
            Free limit resets daily. Come back tomorrow for 3 more tasks.
          </p>
        </div>
      </div>
    </div>
  );
}
